/** @module store/board */

import * as actions from '@/store/modules/board/actions'
import * as getters from '@/store/modules/board/getters'
import * as mutations from '@/store/modules/board/mutations'

import { v1 as uuidv1 } from 'uuid'

const state = () => ({
  config: [],
  measurements: () => {
    return null
  }
})

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export const TYPE_TO_UNIT = {
  index_self_supply: '%',
  index_self_consumption: '%',
  power: 'W',
  power_consumption: 'W',
  power_production: 'W',
  soc: '%',
  temperature: '°C'
}

/**
 * A Group data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.id is the groupId
 * @param {Name} params.name the given Groupname with translations
 * @param {compRule}Array params.compRules are the computation rules of the Group
 * @param {boolean} params.showChildren sets if children should be displayed
 * @param {string} params.icon name of the icon displayed on the node
 * @param {Group}Array params.children List of the childGroups
 *
 */
export function Group({
  id,
  name,
  compRules = [],
  showChildren = null,
  icon = null,
  tag = null,
  permission = null,
  children
}) {
  this.id = id
  this.name = name
  this.compRules = compRules
  this.showChildren = showChildren
  this.icon = icon
  this.tag = tag
  this.permission = permission
  this.children = children
}

/**
 * A CompRule data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.id is the CompRule Id (unique for a group)
 * @param {Name} params.name the given CompRule name with translations
 * @param {Operation} params.operation is the operation, implemented by the CompRule
 * @param {string} params.type is the computation type (e.g. power, soc, temperature...)
 * @param {string} params.unit is the displayed Unit of the result
 *
 */
export function CompRule({ id = null, name, operation, type, unit }) {
  this.id = id ?? uuidv1()
  this.name = name
  this.operation = operation
  this.type = type
  this.unit = unit
}

/**
 * A Operation data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.operator is the key to the used computation function (see: compFs)
 * @param {string}Array params.references list of all keys used for the operation function (has to be null for operator 'eval')
 * @param {EvalTree} params.tree an EvalTree as operation (needs operator 'eval', has to be null for other operators)
 *
 */
export function Operation({ operator, references, tree }) {
  this.operator = operator
  this.references = references
  this.tree = tree
}

/**
 * A EvalTree data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.operator is the key to the used computation function(see treeOperators) (exclusive to reference, value)
 * @param {string} params.reference key for a value (exclusive to operator, value)
 * @param {float} params.value a trivial value (exclusive to operator, reference)
 * @param {EvalTree} params.left 'left' side of an operation (only if operator)
 * @param {EvalTree} params.right 'right' side of an operation (only if operator)
 *
 */
export function EvalTree({ id = null, operator = null, reference = null, value = null, left = null, right = null }) {
  this.id = id ?? uuidv1()
  this.operator = operator
  this.reference = reference
  this.value = value
  this.left = left
  this.right = right
}

/**
 * A Name data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.en holds the english translation
 * @param {string} params.de holds the german translation
 *
 */
export function Name({ en = '', de = '' }) {
  this.en = en
  this.de = de
}

/**
 * A Measurement data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.value holds the value
 * @param {string} params.validity either 'valid'|'incomplete'|'invalid'
 *
 */
export function Measurement({ value = '?', validity = 'invalid' }) {
  this.value = value
  this.validity = validity
}

/**
 * Function Mapping
 *
 * Implements functions based on the given values and a list of keys
 * The keys are referenced over Operations
 *
 */
export const compFs = {
  avg: (vals, operation) => {
    let sum = 0
    let validVals = 0
    const keys = operation.references
    for (const key of keys) {
      if (vals[key] != null) {
        sum += vals[key]
        validVals += 1
      }
    }
    const value = sum / validVals

    return writeArrayMeasurement(value, validVals, keys.length)
  },
  eval: (vals, operation) => {
    return evaluateTree(operation.tree, vals)
  },
  id: (vals, operation) => {
    const keys = operation.references
    if (vals[keys[0]] != null) {
      return new Measurement({ value: vals[keys[0]], validity: 'valid' })
    } else {
      return new Measurement({ value: '?', validity: 'invalid' })
    }
  },
  sum: (vals, operation) => {
    let sum = 0
    let validVals = 0
    const keys = operation.references
    for (const key of keys) {
      if (vals[key] != null) {
        sum += vals[key]
        validVals += 1
      }
    }

    return writeArrayMeasurement(sum, validVals, keys.length)
  }
}

// checks reference array validity
function writeArrayMeasurement(value, validVals, allVals) {
  if (validVals) {
    if (validVals === allVals) {
      return new Measurement({ value, validity: 'valid' })
    } else {
      return new Measurement({ value, validity: 'incomplete' })
    }
  } else {
    return new Measurement({ value: '?', validity: 'invalid' })
  }
}

// Postorder evaluation of an EvalTree
const evaluateTree = (tree, vals) => {
  // Simple Value
  if (tree.value != null) {
    return new Measurement({ value: tree.value, validity: 'valid' })
  }
  // Reference
  if (tree.reference) {
    if (vals[tree.reference] != null) {
      return new Measurement({ value: vals[tree.reference], validity: 'valid' })
    } else {
      return new Measurement({ value: '?', validity: 'invalid' })
    }
  }
  // Operator
  if (tree.operator) {
    let l = new Measurement({ value: '?', validity: 'invalid' })
    if (tree.left) {
      l = evaluateTree(tree.left, vals)
    }
    let r = new Measurement({ value: '?', validity: 'invalid' })
    if (tree.right) {
      r = evaluateTree(tree.right, vals)
    }

    if (l.validity === 'invalid' && r.validity === 'invalid') {
      return new Measurement({ value: '?', validity: 'invalid' })
    }
    return treeOperators[tree.operator](l, r)
  }
  return new Measurement({ value: '?', validity: 'invalid' })
}

// Allowed binary operations on an EvalTree
// Invalid always tested beforehand
const treeOperators = {
  add: (l, r) => {
    return writeMeasurement(l, r, [l.value, r.value, l.value + r.value])
  },
  sub: (l, r) => {
    return writeMeasurement(l, r, [l.value, r.value, l.value - r.value])
  },
  mul: (l, r) => {
    return writeMeasurement(l, r, [l.value, r.value, l.value * r.value])
  },
  div: (l, r) => {
    if (r.value === 0) {
      return new Measurement({ value: '?', validity: 'invalid' })
    }
    return writeMeasurement(l, r, [l.value, 1 / r.value, l.value / r.value])
  }
}

// vals = [<result skipping `r`>, <result skipping `l`>, <results of the comp>]
function writeMeasurement(l, r, vals) {
  if (l.validity === 'invalid') {
    // skip value of `l`
    return new Measurement({ value: vals[1], validity: 'incomplete' })
  }
  if (r.validity === 'invalid') {
    // skip value of `r`
    return new Measurement({ value: vals[0], validity: 'incomplete' })
  }
  return new Measurement({
    value: vals[2],
    validity: l.validity === 'valid' && r.validity === 'valid' ? 'valid' : 'incomplete'
  })
}

// Finding a Group in a Config Array
export const findGroupInConfig = (gId, config) => {
  let g = null
  for (const tree of config) {
    const search = findGroupInConfigTree(gId, tree)
    if (search) {
      g = search
    }
  }
  return g
}

const findGroupInConfigTree = (gId, g) => {
  if (g.id === gId) {
    return g
  }
  for (const child of g.children) {
    const found = findGroupInConfigTree(gId, child)
    if (found) {
      return found
    }
  }
  return null
}

// Finding a Group => CompRule in a Config Array
export const findCompRuleInConfig = (gId, cId, config) => {
  const g = findGroupInConfig(gId, config)

  for (const c of g.compRules) {
    if (c.id === cId) {
      return c
    }
  }
  return null
}

// Finding a Group => CompRule => EvalTreeNode in a Config Array
export const findEvalTreeNodeInConfig = (gId, cId, eId, config) => {
  const c = findCompRuleInConfig(gId, cId, config)

  if (c && c.operation.tree) {
    return findEvalTreeNodeInEvalTree(eId, c.operation.tree)
  }
  return null
}

const findEvalTreeNodeInEvalTree = (id, tree) => {
  if (tree.id === id) {
    return tree
  }
  if (tree.left) {
    const l = findEvalTreeNodeInEvalTree(id, tree.left)
    if (l) {
      return l
    }
  }
  if (tree.right) {
    const r = findEvalTreeNodeInEvalTree(id, tree.right)
    if (r) {
      return r
    }
  }
  return null
}

export const translateAutoConfig = (gs) => {
  for (const g of gs) {
    for (const c of g.compRules) {
      if (!c.name) {
        c.name = new Name({})
      }
      c.operation = translateOperation(c.operation)
    }
    g.children = translateAutoConfig(g.children)
  }

  return gs
}

const translateOperation = (op) => {
  if (op.operator === 'eval') {
    return op
  }
  const newOp = new Operation({
    operator: 'eval',
    references: null,
    tree: null
  })

  if (op.operator === 'id') {
    newOp.tree = new EvalTree({
      reference: op.references[0]
    })
    return newOp
  }

  if (op.operator === 'sum') {
    newOp.tree = createSumEvalTree(op.references)
    return newOp
  }

  if (op.operator === 'avg') {
    const l = op.references.length
    newOp.tree = new EvalTree({
      operator: 'div',
      left: createSumEvalTree(op.references),
      right: new EvalTree({
        value: l
      })
    })
    return newOp
  }
}

const createSumEvalTree = (refs) => {
  if (refs.length === 1) {
    return new EvalTree({
      reference: refs[0]
    })
  }

  const secondHalRefs = refs
  const sepIndex = Math.ceil(refs.length / 2)
  const firstHalfRefs = refs.splice(0, sepIndex)

  return new EvalTree({
    operator: 'add',
    left: createSumEvalTree(firstHalfRefs),
    right: createSumEvalTree(secondHalRefs)
  })
}
